import { useState, useContext, useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import "../../calendar/Calendar.css";
import { DatabaseContext } from "../../../context";
import NewEventDialog from "../../calendar/NewEventDialog";
import NodiantCalendar, {
    getCalendarEventColor,
} from "../../calendar/NodiantCalendar";
import firebase from "../../../firebase";
import { makeid } from "../../../utility";

const getItemKey = (item) =>
    typeof item === "string" ? item : item && item.key;

export default function LinkFileToEventDialog(props) {
    const { events } = useContext(DatabaseContext);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [newEventDialogOpen, setNewEventDialogOpen] = useState(false);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (props.open && !wasOpenRef.current) {
            setSelectedEvents(props.selectedEvents || []);
            setSelectedDate(new Date());
        }

        wasOpenRef.current = props.open;
    }, [props.open, props.selectedEvents]);

    const calendarByKey = useMemo(
        () =>
            new Map(
                (props.calendars || []).map((calendar) => [
                    calendar.key,
                    calendar,
                ]),
            ),
        [props.calendars],
    );

    const visibleEvents = useMemo(() => {
        const visibleEventKeys = new Set();

        (props.calendars || []).forEach((calendar) => {
            (calendar.events || []).forEach((event) => {
                const key = getItemKey(event);
                if (key) {
                    visibleEventKeys.add(key);
                }
            });
        });

        return events.filter((event) => visibleEventKeys.has(event.key));
    }, [events, props.calendars]);

    const eventsOnSelectedDay = useMemo(
        () =>
            visibleEvents
                .filter((event) =>
                    dayjs(event.date).isSame(dayjs(selectedDate), "day"),
                )
                .sort((a, b) => a.name.localeCompare(b.name)),
        [visibleEvents, selectedDate],
    );

    const selectedEventKeys = useMemo(
        () => new Set(selectedEvents.map(getItemKey).filter(Boolean)),
        [selectedEvents],
    );

    // Writes a single leaf on each side of the relationship in one update, so the
    // file and the event can never end up disagreeing about being linked. Writing
    // the whole map back instead would discard any change made in the meantime.
    const persistEventLink = (eventKey, isLinked) => {
        if (!props.file) {
            return;
        }

        const value = isLinked ? true : null;

        firebase
            .database()
            .ref()
            .update({
                [`files/${props.file.key}/events/${eventKey}`]: value,
                [`events/${eventKey}/files/${props.file.key}`]: value,
            });
    };

    const updateSelection = (nextSelectedEvents) => {
        setSelectedEvents(nextSelectedEvents);
        props.selectionChanged(nextSelectedEvents);
    };

    const handleEventSelect = (event) => {
        const isLinked = selectedEventKeys.has(event.key);
        const nextSelectedEvents = isLinked
            ? selectedEvents.filter((item) => getItemKey(item) !== event.key)
            : [...selectedEvents, event];

        updateSelection(nextSelectedEvents);
        persistEventLink(event.key, !isLinked);
    };

    function addEvent(name, selectedCalendar, color) {
        if (!selectedCalendar) {
            return;
        }

        const key = `-${makeid(20)}`;
        const newEvent = {
            key,
            date: dayjs(selectedDate).toString(),
            name,
            calendar: selectedCalendar.key,
            color: color.replace("#", ""),
            files: props.file ? [props.file] : [],
        };

        firebase
            .database()
            .ref("events")
            .child(key)
            .set({
                date: newEvent.date,
                name: newEvent.name,
                calendar: newEvent.calendar,
                color: newEvent.color,
            });

        firebase
            .database()
            .ref(`calendars/${selectedCalendar.key}/events/`)
            .child(key)
            .set(true);

        updateSelection([...selectedEvents, newEvent]);
        persistEventLink(key, true);
        setNewEventDialogOpen(false);
    }

    return (
        <>
            <Dialog
                onClose={() => props.onClose(selectedEvents)}
                aria-labelledby="link-events-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="md"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            minHeight: { md: 680 },
                            maxHeight: "92vh",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <CalendarMonthOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="link-events-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Link to events
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Choose a date, then link this file to one or more
                            events.
                        </Typography>
                    </Box>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label={`${selectedEvents.length} linked`}
                        size="small"
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            backgroundColor: "action.hover",
                            color: "primary.main",
                        }}
                    />
                    <IconButton
                        aria-label="Close event linking"
                        onClick={() => props.onClose(selectedEvents)}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: 0,
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "minmax(0, 1.05fr) minmax(330px, 0.95fr)",
                            },
                            minHeight: { md: 540 },
                        }}
                    >
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3 },
                                borderRight: { md: "1px solid" },
                                borderBottom: {
                                    xs: "1px solid",
                                    md: "none",
                                },
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 1.5,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Select a date
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            backgroundColor: "primary.main",
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Linked event
                                    </Typography>
                                </Box>
                            </Box>

                            <NodiantCalendar
                                selectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                events={visibleEvents}
                                highlightedEventKeys={selectedEventKeys}
                            />

                            {(props.calendars || []).length === 0 && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor:
                                            "rgba(244, 36, 29, 0.07)",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No calendars are available. A calendar
                                        can be created from Hub Management.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box
                            sx={{
                                p: { xs: 2, sm: 3 },
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        sx={{ lineHeight: 1.1 }}
                                    >
                                        {dayjs(selectedDate).format(
                                            "dddd, D MMMM",
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {eventsOnSelectedDay.length === 1
                                            ? "1 available event"
                                            : `${eventsOnSelectedDay.length} available events`}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                        setNewEventDialogOpen(true)
                                    }
                                    disabled={
                                        (props.calendars || []).length === 0
                                    }
                                >
                                    New event
                                </Button>
                            </Box>

                            {eventsOnSelectedDay.length === 0 ? (
                                <Box
                                    sx={{
                                        minHeight: 330,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        color: "text.secondary",
                                        px: 3,
                                    }}
                                >
                                    <EventBusyOutlinedIcon
                                        sx={{
                                            fontSize: 42,
                                            mb: 1.25,
                                            opacity: 0.4,
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: "text.primary",
                                            fontWeight: 800,
                                        }}
                                    >
                                        No events on this date
                                    </Typography>
                                    <Typography variant="body2">
                                        Choose another date or create a new
                                        event.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        maxHeight: { md: 430 },
                                        overflowY: "auto",
                                        pr: { md: 0.5 },
                                    }}
                                >
                                    {eventsOnSelectedDay.map((event) => {
                                        const isLinked =
                                            selectedEventKeys.has(event.key);
                                        const calendar = calendarByKey.get(
                                            event.calendar,
                                        );

                                        return (
                                            <Box
                                                key={event.key}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.25,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: "1px solid",
                                                    borderColor: isLinked
                                                        ? "rgba(0, 149, 61, 0.35)"
                                                        : "divider",
                                                    backgroundColor:
                                                        "background.paper",
                                                    boxShadow: isLinked
                                                        ? "0 2px 10px rgba(0, 149, 61, 0.08)"
                                                        : "none",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        alignSelf: "stretch",
                                                        minHeight: 44,
                                                        borderRadius: 1,
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            getCalendarEventColor(
                                                                event.color,
                                                            ),
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 800,
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {event.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {calendar?.name ||
                                                            "Calendar"}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant={
                                                        isLinked
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    disableElevation
                                                    startIcon={
                                                        isLinked ? (
                                                            <LinkOffIcon />
                                                        ) : (
                                                            <LinkIcon />
                                                        )
                                                    }
                                                    onClick={() =>
                                                        handleEventSelect(event)
                                                    }
                                                >
                                                    {isLinked
                                                        ? "Unlink"
                                                        : "Link"}
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: { xs: "none", sm: "block" } }}
                    >
                        {props.file
                            ? "Changes are saved as you link or unlink events."
                            : "Selections will be applied to this upload."}
                    </Typography>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => props.onClose(selectedEvents)}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <NewEventDialog
                open={newEventDialogOpen}
                onClose={() => setNewEventDialogOpen(false)}
                date={selectedDate}
                addEvent={addEvent}
                calendars={props.calendars || []}
            />
        </>
    );
}
